import browser from "@raiden/library/constants/browser";
import {
  POSTS_TYPE_VALUE_ARTICLE,
  POSTS_TYPE_VALUE_EVENT,
} from "@raiden/library/constants/posts";
import { RESOURCES_VISIBILITY_VALUE_PUBLIC } from "@raiden/library/constants/resources";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUrl";
import { useState } from "react";
import { useIntl } from "react-intl";
import Seo from "../components/Seo";
import { Home } from "../containers/Home";

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getUrlPostsTypeArticle({ environmentId }) {
  return generateApiUrl({
    id: "@api.posts.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_ARTICLE],
      visibility: RESOURCES_VISIBILITY_VALUE_PUBLIC,
      page: 1,
      per_page: 5,
      fields: [
        "post.current_revision",
        "post.revision.cover",
        "post.categories",
      ],
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getUrlPostsTypeEventCategories({ environmentId }) {
  return generateApiUrl({
    id: "@api.postsCategories.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_EVENT],
      page: 1,
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 * @param {string} [params.categoryId]
 */
function getUrlPostsTypeEvent({ environmentId, categoryId }) {
  return generateApiUrl({
    id: "@api.posts.viewAny",
    query: {
      environment_id: [environmentId],
      category_id: [categoryId],
      type: [POSTS_TYPE_VALUE_EVENT],
      page: 1,
      per_page: 10,
      fields: [
        "post.current_revision",
        "post.categories",
        "post.revision.cover",
      ],
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getUrlSocialsPosts({ environmentId }) {
  return generateApiUrl({
    id: "@api.socials.posts.viewAny",
    query: {
      environment_id: [environmentId],
      network: ["facebook"],
      sort: "published_desc",
      page: 1,
      per_page: 8,
      fields: ["social.post.pictures", "social.post.publications"],
    },
  });
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>} [articlesResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>} [eventCategoriesResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>} [eventsResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/social/Post").Post[]>} [socialsPostsResponse]
 */
/**
 * @param {Props} props
 */
export default function Page({
  articlesResponse,
  eventCategoriesResponse,
  eventsResponse,
  socialsPostsResponse,
}) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  const [categoryId, setCategoryId] = useState("");

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>} */
  const { swrResponse: swrResponseArticles } = useApi(
    bestEnvironment
      ? getUrlPostsTypeArticle({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: articlesResponse,
      },
    },
  );
  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/PostCategory").PostCategory[]>} */
  const { swrResponse: swrResponseEventCategories } = useApi(
    bestEnvironment
      ? getUrlPostsTypeEventCategories({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: eventCategoriesResponse,
      },
    },
  );
  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>} */
  const { swrResponse: swrResponseEvents } = useApi(
    bestEnvironment
      ? getUrlPostsTypeEvent({
          environmentId: bestEnvironment.id,
          categoryId,
        })
      : null,
    {
      swrConfig: {
        fallbackData: eventsResponse,
      },
    },
  );
  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/social/Post").Post[]>} */
  const { swrResponse: swrResponseSocialsPosts } = useApi(
    bestEnvironment
      ? getUrlSocialsPosts({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: socialsPostsResponse,
      },
    },
  );

  return (
    <>
      <Seo
        title={intl.formatMessage({
          defaultMessage: "Accueil",
        })}
        links={(locale) =>
          generateFrontUri({
            id: "@front.home",
            parameters: {
              locale,
            },
          })
        }
      />

      <Home
        articlesResponse={swrResponseArticles}
        eventCategoriesResponse={swrResponseEventCategories}
        eventsResponse={swrResponseEvents}
        socialsPostsResponse={swrResponseSocialsPosts}
        categoryId={categoryId}
        setCategoryId={setCategoryId}
      />
    </>
  );
}

/**
 * @typedef {import("../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  if (!browser) {
    const bestEnvironment =
      context.configuration?.environments?.length === 1
        ? context.configuration.environments[0]
        : undefined;

    if (bestEnvironment) {
      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]> | undefined} */
      values.articlesResponse = await serverApiFetcher(
        getUrlPostsTypeArticle({ environmentId: bestEnvironment.id }),
      ).catch(() => {});

      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]> | undefined} */
      values.eventCategoriesResponse = await serverApiFetcher(
        getUrlPostsTypeEventCategories({ environmentId: bestEnvironment.id }),
      ).catch(() => {});

      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]> | undefined} */
      values.eventsResponse = await serverApiFetcher(
        getUrlPostsTypeEvent({
          environmentId: bestEnvironment.id,
        }),
      ).catch(() => {});
    }
  }

  return values;
};
